// src/redux/middleware/listenerRegistry.ts

import { Cell, Row } from "../types";

export type RowAddListener = (tableId: string, row: Row) => void;
export type RowsAddListener = (tableId: string, rows: Row[]) => void;
export type RowUpdateListener = (tableId: string, rows: Row[]) => void;
export type CellUpdateListener = (tableId: string, cell: Cell) => void;

class ListenerRegistry {
    private rowAddListeners: Map<string, Set<RowAddListener>> = new Map();
    private rowsAddListeners: Map<string, Set<RowsAddListener>> = new Map();
    private rowUpdateListeners: Map<string, Set<RowUpdateListener>> = new Map();
    private cellUpdateListeners: Map<string, Set<CellUpdateListener>> = new Map();

    // Add a listener to a specific tableId with the specified listener set type
    private addListener<T>(map: Map<string, Set<T>>, tableId: string, listener: T) {
        if (!map.has(tableId)) {
            map.set(tableId, new Set());
        }
        map.get(tableId)!.add(listener);
    }

    // Remove a listener from a specific tableId and clean up if the set is empty
    private removeListener<T>(map: Map<string, Set<T>>, tableId: string, listener: T) {
        const listeners = map.get(tableId);
        if (listeners) {
            listeners.delete(listener);
            if (listeners.size === 0) map.delete(tableId);
        }
    }

    // Retrieve listeners for a specific tableId
    private getListeners<T>(map: Map<string, Set<T>>, tableId: string): Set<T> {
        return map.get(tableId) || new Set();
    }

    // 행 추가 리스너 추가, 제거, 가져오기를 위한 공개 API
    addRowAddListener(tableId: string, listener: RowAddListener) {
        this.addListener(this.rowAddListeners, tableId, listener);
    }

    removeRowAddListener(tableId: string, listener: RowAddListener) {
        this.removeListener(this.rowAddListeners, tableId, listener);
    }

    addRowsAddListener(tableId: string, listener: RowsAddListener) {
        this.addListener(this.rowsAddListeners, tableId, listener);
    }

    removeRowsAddListener(tableId: string, listener: RowsAddListener) {
        this.removeListener(this.rowsAddListeners, tableId, listener);
    }

    getRowsAddListeners(tableId: string): Set<RowsAddListener> {
        return this.getListeners(this.rowsAddListeners, tableId);
    }

    getRowAddListeners(tableId: string): Set<RowAddListener> {
        return this.getListeners(this.rowAddListeners, tableId);
    }

    // 행 업데이트 리스너 추가, 제거, 가져오기를 위한 공개 API
    addRowUpdateListener(tableId: string, listener: RowUpdateListener) {
        this.addListener(this.rowUpdateListeners, tableId, listener);
    }

    removeRowUpdateListener(tableId: string, listener: RowUpdateListener) {
        this.removeListener(this.rowUpdateListeners, tableId, listener);
    }

    getRowUpdateListeners(tableId: string): Set<RowUpdateListener> {
        return this.getListeners(this.rowUpdateListeners, tableId);
    }

    // 셀 업데이트 리스너 추가, 제거, 가져오기를 위한 공개 API
    addCellUpdateListener(tableId: string, listener: CellUpdateListener) {
        this.addListener(this.cellUpdateListeners, tableId, listener);
    }

    removeCellUpdateListener(tableId: string, listener: CellUpdateListener) {
        this.removeListener(this.cellUpdateListeners, tableId, listener);
    }

    getCellUpdateListeners(tableId: string): Set<CellUpdateListener> {
        return this.getListeners(this.cellUpdateListeners, tableId);
    }
}

const listenerRegistry = new ListenerRegistry();
export default listenerRegistry;
